<template>
    <div class="home">
        <b-container fluid v-if="application">
            <b-row>
                <b-col>
                    <h1>{{profile.firstName}}'s {{certificate.type.description}} Renewal Application View</h1>
                    <p>Welcome to your application view.<br/>If you need to contact MDH you can do so via the "Messages to/from MDH" section.
                        Any messages to and from MDH will appear in the table. Any new messages will be highlighted in yellow.
                    </p>
                </b-col>
            </b-row>

            <div class="card">
                <div class="card-header">Application Status</div>
                <div class="card-body">
                    <b-row>
                        <b-col cols="3">
                            <p class="mb-1">Application Status</p>
                            {{application.status.description}}
                        </b-col>
                        <b-col cols="3">
                            <p class="mb-1">Received Date</p>
                            {{formatDateTime(application.submittedDate)}}
                        </b-col>
                        <b-col cols="3">
                            <p class="mb-1">Approved Date</p>
                            {{formatDate(application.approvedDate)}}
                        </b-col>
                        <b-col cols="3">
                            <p class="mb-1">Expiration Date</p>
                            {{formatDate(application.expirationDate)}}
                        </b-col>
                    </b-row>
                    <b-row v-if="application.status.description==='Denied'">
                        <b-col>
                            <BaseTextOutput label="Denial Reason" :value="application.denialReason.code==='OTH'?application.otherDenialReason:application.denialReason.description"/>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <div class="card">
                <div class="card-header">Your personal information</div>
                <div class="card-body">
                    <b-row>
                        <b-col cols="12">
                            <BaseTextOutput label="Name" :value="profile.fullName"/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12">
                            <BaseTextOutput label="Address" :value="profile.contact.address.streetAddress+', '+profile.contact.address.city+', '
                            +profile.contact.address.state+' '+profile.contact.address.postalCode"/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">
                            <BaseTextOutput label="Cell Phone Number" :value="profile.contact.phone"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Email Address" :value="profile.contact.email"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Date Of Birth" :value="formatDate(profile.dateOfBirth)"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Social Security Number" :value="profile.ssn"/>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <div class="card">
                <div class="card-header">Messages to/from MDH</div>
                <div class="card-body">
                    <b-row class="mt-2">
                        <b-col cols="12">
                            <b-table hover small show-empty class="sr-caption"
                                     aria-live="polite"
                                     responsive="true"
                                     :fields="noteFields"
                                     :items="notes"
                                     :busy="false"
                                     label-sort-asc=""
                                     label-sort-desc=""
                                     :tbody-tr-class="highlightUnread"
                                     caption="Messages sent to/from MDH table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                                <template v-slot:no-data>
                                    No Messages Found.
                                </template>
                                <div slot="table-busy" class="text-center text-danger my-2">
                                    <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                                <template slot="empty" slot-scope="scope">
                                    No Messages Found.
                                </template>
                                <template v-slot:cell(noteType)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">From MDH</div>
                                    <div v-else-if="row.item && row.item.noteType.code==='EXTIN'">To MDH</div>
                                    <div v-else>{{row.item.noteType.description}}</div>
                                </template>
                                <template v-slot:cell(noteDate)="row">
                                    {{formatDateTime(row.item.noteDate)}}
                                </template>
                                <template v-slot:cell(noteByName)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">MDH Staff</div>
                                    <div v-else>{{row.item.noteByName}}</div>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3" v-if="isActionable">
                        <b-col cols="12">
                            <BaseParagraphInput v-model="newNote"
                                                :v="$v.newNote"
                                                label="Send a Message to MDH"
                                                slug="newMessage"
                                                tip="If you wish to send a message to MDH you can do it here."
                                                setId="newMessage"
                            />
                            <b-button size="sm"
                                      variant="primary"
                                      class="mr-1"
                                      @click="showNoteModal(newNote)"
                                      target="_attach"
                            >
                                Send
                            </b-button>
                        </b-col>
                    </b-row>
            </div>
        </div>
        <div class="card">
            <div class="card-header">Employment Information and Attachments</div>
            <div class="card-body">
                <div v-for="(employer, index) in application.employers" v-bind:key="index">
                  <b-row>
                    <b-col>
                      <h2>Employer {{index+1}} of {{application.employers.length}}:</h2>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <BaseTextOutput label="Facility Name" :value="employer.name"/>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col >
                      <BaseTextOutput label="Address" :value="employer.address.streetAddress"/>
                    </b-col>
                    <b-col >
                      <BaseTextOutput label="City" :value="employer.address.city"/>
                    </b-col>
                    <b-col>
                      <BaseTextOutput label="State" :value="employer.address.state"/>
                    </b-col>
                    <b-col>
                      <BaseTextOutput label="Zip" :value="employer.address.postalCode"/>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <h3>Attachments:</h3>
                    </b-col>
                  </b-row>
                    <b-row v-for="(attachment, index) in employer.attachments" v-bind:key="index">
                      <b-col>
                        <BaseTextOutput label="File name" :value="attachment.filename"/>
                      </b-col>
                      <b-col>
                        <BaseTextOutput label="Attachment Type" :value="attachment.type.description"/>
                      </b-col>
                      <b-col v-if="attachment.otherType">
                        <BaseTextOutput v-if="attachment.type.code==='PS'" label="Paystub kind" :value="attachment.otherType"/>
                        <BaseTextOutput v-else-if="attachment.type.code==='W2'" label="W-2 year(s)" :value="attachment.otherType"/>
                        <BaseTextOutput v-else label="Other attachment type" :value="attachment.otherType"/>
                      </b-col>
                      <b-col>
                        <b-button variant="primary" @click="download(attachment)">Download</b-button>
                      </b-col>
                    </b-row>
                </div>
            </div>
        </div>
        <div class="card" v-if="isActionable">
            <div class="card-header">Application corrections</div>
            <div class="card-body">
                <b-row>
                    <b-col>
                        <p>
                            If you found any errors or mistakes in your application you can correct them and resubmit the application here.
                        </p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button variant="primary" @click="showCorrectionsModal">Correct application</b-button>
                    </b-col>
                </b-row>
            </div>
        </div>
        </b-container>
        <b-modal id="note-modal" hide-header hide-footer>
            <h2>Confirm sending Message</h2>
            <hr/>
            <form ref="form" @submit.prevent="attachNewNote">
                You are about to send a new message.
                <p class="mt-2">Press Confirm to attach this note or Cancel to return to the application view.</p>
                <div>
                    <b-button @click="closeNoteModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="corrections-modal" hide-header hide-footer>
            <h2>Open corrections page</h2>
            <hr/>
            <form ref="form" @submit.prevent="openCorrections">
                You are about to open corrections for this application.
                <p class="mt-2">Press Confirm to proceed to the corrections page or Cancel to return to the application view.</p>
                <div>
                    <b-button @click="closeCorrectionsModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
    import BaseTextOutput from "@/components/templates/input/BaseTextOutput";
    import BaseParagraphInput from "@/components/templates/input/BaseParagraphInput";
    import EmployersList from "@/components/templates/EmployersList";
    import axios from 'axios';
    import maxLength from "vuelidate/src/validators/maxLength";
    import moment from 'moment';
    import {toastService} from "@/mixins/toastService";

    export default {
        name: "RenewalReview",
        components:{
            BaseTextOutput,
            BaseParagraphInput,
        },
        mixins: [
            toastService
        ],
        data:()=>({
            application: null,
            notes: [],
            noteFields: [
                {key: 'note', label: 'Message',sortable:true},
                {key: 'noteType',label:'Message Type', sortable:true},
                {key: 'noteDate',label:'Message Date',sortable:true},
                {key:'noteByName',sortable:true,label:'Message By'},
            ],
            newNote: '',
            attachmentTypeOptions: [],
            profile: null,
            certificate: null,
        }),
        created(){
            this.loadApplication()
            this.loadAttachmentTypes()
            setTimeout(this.markNotesAsRead,5000);
        },
        methods:{
            loadApplication(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/renewal/${this.$route.params.id}`)
                    .then(response=>{
                        this.application = response.data.application
                        this.profile = response.data.profile
                        this.certificate = response.data.certificate
                        this.notes = this.application.external
                    })
                    .catch(error=>{
                        toastService.showRootToast('Loading application failed','','danger'), this.$root;
                    })
            },
            loadNotes(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/notes/${this.$route.params.id}`)
                    .then(response=>{
                        this.notes=response.data;
                    })
                    .catch(error=>{
                        toastService.showRootToast('Loading notes failed','','danger', this.$root);
                    })
            },
            loadAttachmentTypes(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/supplementalTypes`)
                    .then(response=>{
                        for(let type of response.data){
                            this.attachmentTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                    })
                    .catch(error=>{
                        toastService.showRootToast('Loading attachment types failed','','danger', this.$root);
                    })
            },
            markNotesAsRead(){
                axios.put(`${process.env.VUE_APP_API_BASE_URL}/application/notes/${this.$route.params.id}/readUser`)
                    .catch(error=>{
                        toastService.showRootToast('Marking notes as read failed','','danger', this.$root);
                    })
            },

            showNoteModal(){
                this.$bvModal.show('note-modal');
            },
            closeNoteModal(){
                this.$bvModal.hide('note-modal');
            },
            showCorrectionsModal(){
                this.$bvModal.show('corrections-modal');
            },
            closeCorrectionsModal(){
                this.$bvModal.hide('corrections-modal');
            },
            openCorrections(){
                this.$router.push({path: `/resubmit-renewal/${this.$route.params.id}`})
            },

            attachNewNote(){
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/notes/${this.$route.params.id}/addFromUser`,this.newNote)
                    .then(response => {
                        toastService.showRootToast('Message Sent', 'Your message has been successfully sent to MDH.','success', this.$root);
                        this.newNote='';
                        this.loadNotes();
                    })
                    .catch(error=>{
                        toastService.showRootToast('Error', 'Message sending failed','danger', this.$root)
                    })
                    .finally(()=>{
                        this.closeNoteModal();
                    })
            },
            reloadAttachments(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/get/${this.$route.params.id}`)
                    .then(response=>{
                        this.application.attachments = response.data
                    })
                    .catch(error=>{
                        toastService.showRootToast('Reloading attachments failed','','danger', this.$root);
                    })
            },
            highlightUnread(item,type){
                return (item != null && item.noteType.code==='EXT' && item.noteRead.code==="UNREAD") ? 'table-warning' : '';
            },

            formatDate(unformattedDate){
                let formattedDate = moment(unformattedDate);
                if(!formattedDate.isValid()) return '';
                return formattedDate.format("MM/DD/YYYY")
            },
            formatDateTime(noteDateTime){
                return moment(noteDateTime).format('MM/DD/YYYY hh:mmA');
            },
            download(attachment){
              axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/getUrl/`+attachment.attachmentId)
                  .then(response=>{
                    if(response.data){
                      window.open(response.data,"_blank");
                    }
                    else{this.$emit('attachment-get-failure')}
                  })
            }


        },
        validations: {
            newNote: { required: false,  maxLength: maxLength(4000)},
        },
        computed:{
            isActionable: function(){
                let statuses = ["Received","In Progress","Reopened","Resubmitted","Returned"];
                return statuses.some(s => s === this.application.status.description)
            },
        }

    }
</script>

<style scoped>
    p {
        font-weight: 700;
    }

</style>